import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Img from "@insite/mobius/Img";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import submitFeedbackModal from "@tCentro/Handlers/CENProductDetailsFeedbackModalHandler";
import Megaphone from "@tCentro/Icons/Megaphone";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    modalIsOpen: state.components.addToListModal.isOpen,
    ContactUsPageUrl: getPageLinkByPageType(state, "ContactUSPage")?.url || "",
    userName: state.context.session.userName,
    currentUserIsGuest: getCurrentUserIsGuest(state),
});

const mapDispatchToProps = {
    submitFeedbackModal,
};

interface OwnProps {
    product: ProductModel;
    image: ImageModel;
    useLargeImage?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProduct &
    HasOnSuccess;

export interface NISProductDetailsFeedbackModalStyles {
    modal?: ModalPresentationProps;
    btnWrapper?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    fieldWrapper?: InjectableCss;
    txtfld?: InjectableCss;
    txtfld2?: InjectableCss;
    contentWrapper?: InjectableCss;
    imgWrapper?: InjectableCss;
    img?: InjectableCss;
    middleContentWrapper?: InjectableCss;
    feedback?: InjectableCss;
    txtFldWrapper?: InjectableCss;
    productFeedbackStyles?: InjectableCss;
}

export const nisProductDetailsFeedbackModalStyles: NISProductDetailsFeedbackModalStyles = {
    modal: {
        cssOverrides: {
            modalTitle: css`
                h4 {
                    font-size: 24px !important;
                }
            `,
        },
    },
    submitButton: {
        css: css`
            border-radius: 24px !important;
        `,
    },
    btnWrapper: {
        css: css`
            padding-top: 40px;
        `,
    },

    cancelButton: {
        variant: "secondary",
        css: css`
            margin-left: 16px;
            border-radius: 24px;
        `,
    },
    fieldWrapper: {
        css: css`
            display: flex;
        `,
    },
    txtfld: {
        css: css`
            border-radius: 40px !important;
            width: 95% !important;
            background-color: #f2f2f2 !important;
        `,
    },
    txtfld2: {
        css: css`
            width: 98% !important;
            border-radius: 40px !important;
            height: 80px !important;
            background-color: #f2f2f2 !important;
        `,
    },
    contentWrapper: {
        css: css`
            display: flex;
            justify-content: end;
            margin-right: 24px;
            span {
                font-family: Roboto;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-right: 0px;
                    `,
                    css`
                        margin-right: 0px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    imgWrapper: {
        css: css`
            display: flex;
            margin: 40px 0 24px 0;
        `,
    },
    img: {
        css: css`
            width: 80px;
            height: 80px;
            box-shadow:
                0px 0px 1px rgba(38, 37, 37, 0.56),
                0px 2px 8px rgba(38, 37, 37, 0.16);
        `,
    },
    middleContentWrapper: {
        css: css`
            margin-left: 24px;
        `,
    },
    txtFldWrapper: {
        css: css`
            margin-top: 28px;
        `,
    },
    productFeedbackStyles: {
        css: css`
            margin-right: 10px;
        `,
    },
};

const styles = nisProductDetailsFeedbackModalStyles;
const StyledForm = getStyledWrapper("form");
const emailRegexp = new RegExp("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.(?!usa$)[a-zA-Z]{2,}$", "i");

const NISProductDetailsFeedbackModal = ({ submitFeedbackModal, product, currentUserIsGuest, userName }: Props) => {
    const nameRequiredFieldMessage = siteMessage("Name is Required");
    const emailRequiredFieldMessage = siteMessage("Enter a Valid Email Address");
    const feedbackRequiredFieldMessage = siteMessage("Enter your Feedback");

    const imagePath = product.smallImagePath || product.mediumImagePath || product.largeImagePath;

    const [name, setName] = useState("");
    const [nameError, setNameError] = useState(nameRequiredFieldMessage);
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [feedback, setFeedback] = useState("");
    const [feedbackError, setFeedbackError] = useState(feedbackRequiredFieldMessage);
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const toasterContext = useContext(ToasterContext);

    const validateName = (name: string) => {
        const errorMessage = !name ? nameRequiredFieldMessage : "";
        setNameError(errorMessage);
        return !errorMessage;
    };
    const validateEmail = (email: string) => {
        const errorMessage = !email
            ? emailRequiredFieldMessage
            : emailRegexp.test(email)
              ? ""
              : emailRequiredFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };
    const validateFeedback = (name: string) => {
        const errorMessage = !name ? emailRequiredFieldMessage : "";
        setFeedbackError(errorMessage);
        return !errorMessage;
    };
    const validateSubmitEnabled = () => {
        return !(nameError || emailError || feedbackError);
    };

    const nameChangeHandler = (value: string) => {
        validateName(value);
        setName(value);
    };
    const emailChangeHandler = (value: string) => {
        const sanitizedValue = value.replace(/\s+/g, "");
        validateEmail(sanitizedValue);
        setEmail(sanitizedValue);
    };
    const feedbackChangeHandler = (value: string) => {
        validateFeedback(value);
        setFeedback(value);
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (!validateSubmitEnabled()) {
            return;
        }

        setIsSubmitting(true);

        submitFeedbackModal({
            name,
            feedback,
            emailAddress: email,
            productNumber: product?.productNumber,
        });
        modalCloseHandler();
        toasterContext.addToast({ body: translate("Successfully submitted!"), messageType: "success" });
        clearFormFields();
    };

    const clearFormFields = () => {
        setName("");
        setEmail("");
        setFeedback("");
        setIsSubmitted(false);
        setIsSubmitting(false);
        setNameError(nameRequiredFieldMessage);
        setEmailError(emailRequiredFieldMessage);
        setFeedbackError(feedbackRequiredFieldMessage);
    };

    const [modalIsOpen, setModalIsOpen] = useState(false);

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const openModal = () => {
        setModalIsOpen(true);
    };

    return !(!userName || (userName && currentUserIsGuest)) ? (
        <>
            <StyledWrapper {...styles.contentWrapper}>
                <Icon src={Megaphone} />
                <Typography {...styles.productFeedbackStyles}>Product Feedback?</Typography>
                <Link onClick={openModal}>Help us improve.</Link>
            </StyledWrapper>
            <Modal
                {...styles.modal}
                headline={translate("Help us get it right.")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <StyledForm onSubmit={submitHandler}>
                    <StyledWrapper>
                        Your feedback is important to us. Please leave us a message below to help us improve our
                        experience. For product and catalog inquiries, product support, and more,{" "}
                        <Link href="/contactus"> contact us here </Link> instead.
                    </StyledWrapper>
                    <StyledWrapper {...styles.imgWrapper}>
                        <Img src={imagePath} altText={product.productTitle} {...styles.img} />
                        <StyledWrapper {...styles.middleContentWrapper}>
                            <Typography variant="h6">Leaving feedback for: </Typography>
                            <StyledWrapper>{product.productTitle}</StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.fieldWrapper}>
                        <TextField
                            {...styles.txtfld}
                            label={translate("Name *")}
                            onChange={e => nameChangeHandler(e.currentTarget.value)}
                            value={name}
                            error={isSubmitted && nameError}
                        />
                        <TextField
                            {...styles.txtfld}
                            label={translate("Email Address *")}
                            onChange={e => emailChangeHandler(e.currentTarget.value)}
                            value={email}
                            error={isSubmitted && emailError}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.txtFldWrapper}>
                        <TextField
                            {...styles.txtfld2}
                            label={translate("How can we improve this page? *")}
                            onChange={e => feedbackChangeHandler(e.currentTarget.value)}
                            value={feedback}
                            error={isSubmitted && feedbackError}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.btnWrapper}>
                        <Button type="submit" {...styles.submitButton}>
                            {translate("Submit")}
                        </Button>
                        <Button {...styles.cancelButton} onClick={modalCloseHandler}>
                            {translate("Cancel")}
                        </Button>
                    </StyledWrapper>
                </StyledForm>
            </Modal>
        </>
    ) : null;
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(NISProductDetailsFeedbackModal)),
    definition: {
        group: "Product Details",
    },
};

export default widgetModule;
